import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { Skeleton } from '@/components/ui/skeleton'; // Import Skeleton
import { formatSats, formatTokenValue } from '@/lib/formatters';
import type { AddressSummaryResponse } from '@/lib/hooks/useAPI';

interface BalanceOverviewProps {
  balanceData: AddressSummaryResponse; // Changed from BalanceResponse to Balance
}

export function BalanceOverviewSkeleton() {
  return (
    <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-3 mb-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Value</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-32 mb-1" />
          <Skeleton className="h-4 w-28" />
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">BTC Balance</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-36 mb-1" />
          <Skeleton className="h-4 w-40 mb-1" />
          <Skeleton className="h-4 w-24" />
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Token Value</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-28 mb-1" />
          <Skeleton className="h-4 w-36" />
        </CardContent>
      </Card>
    </div>
  );
}

export function BalanceOverview({ balanceData }: BalanceOverviewProps) {
  return (
    <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-3 mb-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Value</CardTitle>
          {/* Icon optional */}
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-mono font-bold">
            {formatTokenValue(balanceData.balance.btcValueUsdSoft)}
          </div>
          <p className="text-xs font-mono text-muted-foreground">
            {formatTokenValue(balanceData.balance.btcValueUsdHard)} Available (Hard){' '}
            {/* Use hardBalance */}
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">BTC Balance</CardTitle>
          {/* BTC Icon? */}
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-mono font-bold">
            {formatSats(balanceData.balance.btcSoftBalanceSats)} BTC {/* Use softBalance */}
          </div>
          <p className="text-xs font-mono text-muted-foreground">
            {formatSats(balanceData.balance.btcHardBalanceSats)} BTC Available (Hard){' '}
            {/* Use hardBalance */}
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Token Value</CardTitle>
          {/* Token Icon? */}
        </CardHeader>
        <CardContent>
          {/* Using MOCK value for now */}
          <div className="text-2xl font-mono font-bold">{formatTokenValue(0)}</div>
          <p className="text-xs text-muted-foreground">Total value of all tokens</p>
          <p className="text-xs text-muted-foreground">
            {balanceData.tokens?.length} {balanceData.tokens?.length !== 1 ? 'Tokens' : 'Token'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
